import { useCreateStory } from "@/providers/hooks";
import {
  type CreateTarget,
  standaloneVersionEditorPath,
  useCreateStandaloneVersion,
} from "@/features/standalone-versions";
import type { IPage } from "@/types";
import Button from "@components/ui/button";
import { CardFooter } from "@components/ui/card";
import type { StoryType } from "@config";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const CreateStoryFooter = ({
  close,
  name,
  templateStory,
  folderId,
  forceUseTemplateSlides,
  keepTemplateDesignSystem,
  createTarget = "story",
  onSuccess,
  loading,
  setLoading,
}: {
  close: () => void;
  name: string;
  templateStory: IPage;
  folderId: string | null;
  forceUseTemplateSlides: boolean;
  keepTemplateDesignSystem?: boolean;
  /** A story (default), or a standalone version; see features/standalone-versions. */
  createTarget?: CreateTarget;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  onSuccess?: (story: StoryType) => void;
}) => {
  const handleCreateStory = useCreateStory();
  const createStandaloneVersion = useCreateStandaloneVersion();
  const navigate = useNavigate();
  const noun = createTarget === "version" ? "version" : "story";

  const submit = useCallback(async () => {
    try {
      setLoading(true);
      if (createTarget === "version") {
        const made = await createStandaloneVersion({
          template: templateStory as unknown as StoryType,
          title: name,
          origin: { kind: "slides" },
          keepTemplateDesignSystem,
        });
        navigate(standaloneVersionEditorPath(made.page._id, made.version._id));
        setLoading(false);
        close();
        return;
      }
      const created = await handleCreateStory({
        status: "Draft",
        title: name,
        template: templateStory,
        type: "create",
        folderId: folderId || null,
        forceUseTemplateSlides,
        keepTemplateDesignSystem,
      });

      if (!created) {
        setLoading(false);
        return;
      }

      if (onSuccess) onSuccess(created as StoryType);
      else navigate(`/pages/editor/${created._id}`);
    } catch (error) {
      console.error(error);
      // The version path already toasts; the story path did not.
      if (createTarget !== "version") toast.error("Failed to create story");
    }
    setLoading(false);
    close();
  }, [
    templateStory,
    close,
    setLoading,
    name,
    handleCreateStory,
    createStandaloneVersion,
    createTarget,
    onSuccess,
    navigate,
    folderId,
    forceUseTemplateSlides,
    keepTemplateDesignSystem,
  ]);

  return (
    <CardFooter className="tw-justify-end tw-gap-4">
      <Button variant="secondary" onClick={close}>
        Go back
      </Button>
      <Button onClick={submit} disabled={!name} isLoading={loading}>
        {loading ? `Creating ${noun}...` : `Create ${noun}`}
      </Button>
    </CardFooter>
  );
};
